import React, { useMemo, useState, lazy, Suspense, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Accordion,
    Badge,
    Loader,
    Text,
    Title,
    Group
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import type { WorkflowStep } from './types';
import { ConfigProp } from './SettingsBuilder';

// Lazy load ACE editor
const AceEditor = lazy(() => import('react-ace'));

// Import Ace editor modules
import 'ace-builds';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';

interface WorkflowStepsViewerProps {
    workflow: any; // The workflow entity
}

/**
 * Read-only viewer for workflow steps and configuration
 * Displays the workflow structure similar to WorkflowDesigner but without edit capabilities
 */
export const WorkflowStepsViewer: React.FC<WorkflowStepsViewerProps> = ({
    workflow
}) => {
    const { colorScheme } = useMantineColorScheme();
    const [expandedSteps, setExpandedSteps] = useState<string[]>([]);
    const hasInitialized = useRef(false);

    // Parse workflow config to get steps and zSteps
    const { steps, zSteps, stepAliases } = useMemo(() => {
        let steps: WorkflowStep[] = [];
        let zSteps: Record<string, any> = {};
        const aliases = new Map<string, string>();

        try {
            const rawSettings = workflow?.Settings;
            const config = (typeof rawSettings === 'string' ? rawSettings : rawSettings != null ? JSON.stringify(rawSettings) : '') || '{}';
            if (config.trim() !== '' && config.trim() !== '{}') {
                const configObj = JSON.parse(config);
                
                // Get zSteps (full copies of activities)
                zSteps = configObj.zSteps || {};
                
                // Get steps array (references with overrides)
                const stepsArray = configObj.steps || [];
                
                // Build steps list from the steps array
                steps = stepsArray.map((stepRef: any) => {
                    // Extract stepId - handle both string and object cases
                    const stepId = typeof stepRef.stepId === 'object' 
                        ? (stepRef.stepId.Id || stepRef.stepId.$ref || String(stepRef.stepId))
                        : (stepRef.stepId || '');
                    const fullStep = zSteps[stepId];
                    
                    if (fullStep) {
                        // Helper to normalize reference objects to strings
                        const normalizeValue = (value: any): any => {
                            if (value === null || value === undefined) return value;
                            if (typeof value === 'object' && !Array.isArray(value)) {
                                // Check if it's a reference object (has $ref or common reference keys)
                                if ('$ref' in value || ('Id' in value && ('DisplayName' in value || 'EntityType' in value))) {
                                    // It's a reference object - extract the string value
                                    return value.DisplayName || value.Id || value.EntityType || value.$ref || String(value);
                                }
                                // It's a regular object, keep it as-is (like Config, valueOverrides, etc.)
                                return value;
                            }
                            return value;
                        };
                        
                        // Normalize all properties from fullStep that might be reference objects
                        const normalizedStep: any = {
                            Id: String(stepId), // Always ensure Id is a string
                            stepAlias: stepRef.stepAlias,
                            valueOverrides: stepRef.valueOverrides
                        };
                        
                        // Copy and normalize all properties from fullStep
                        Object.keys(fullStep).forEach(key => {
                            const value = fullStep[key];
                            // Skip properties that should remain as objects (Config, valueOverrides, etc.)
                            if (['Config', 'valueOverrides', 'props', 'zSteps', 'steps'].includes(key)) {
                                normalizedStep[key] = value;
                            } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                // Check if it's a reference object (has $ref or common reference keys)
                                if ('$ref' in value || ('Id' in value && ('DisplayName' in value || 'EntityType' in value))) {
                                    // It's a reference object - normalize it
                                    normalizedStep[key] = normalizeValue(value);
                                } else {
                                    // It's a regular object, keep it as-is
                                    normalizedStep[key] = value;
                                }
                            } else {
                                // Primitive value or array, copy as-is
                                normalizedStep[key] = value;
                            }
                        });
                        
                        return normalizedStep as WorkflowStep;
                    } else {
                        // Fallback if zSteps doesn't have this step
                        return {
                            Id: stepId,
                            DisplayName: stepRef.stepAlias || `Step ${stepId}`,
                            stepAlias: stepRef.stepAlias,
                            valueOverrides: stepRef.valueOverrides
                        } as WorkflowStep;
                    }
                });
                
                // Extract aliases - ensure step.Id is a string
                steps.forEach(step => {
                    if (step.stepAlias) {
                        const stepIdValue = (step as any).Id;
                        const stepId = typeof stepIdValue === 'object' && stepIdValue !== null
                            ? (stepIdValue.Id || stepIdValue.$ref || String(stepIdValue))
                            : String(stepIdValue || '');
                        aliases.set(stepId, step.stepAlias);
                    }
                });
            } else {
                // Fallback to legacy Steps array if Config is empty
                const legacySteps = workflow?.Steps || [];
                if (Array.isArray(legacySteps) && legacySteps.length > 0) {
                    // Helper to normalize reference objects to strings
                    const normalizeValue = (value: any): any => {
                        if (value === null || value === undefined) return value;
                        if (typeof value === 'object' && !Array.isArray(value)) {
                            return value.DisplayName || value.Id || value.EntityType || value.$ref || String(value);
                        }
                        return value;
                    };
                    
                    steps = legacySteps.map((step: any, index: number) => {
                        if (typeof step === 'string') {
                            return {
                                Id: step,
                                DisplayName: `Step ${index + 1}`
                            } as WorkflowStep;
                        }
                        // Normalize step properties if they're objects
                        const normalizedStep: any = { ...step };
                        normalizedStep.Id = normalizeValue(step.Id || step.id);
                        normalizedStep.DisplayName = normalizeValue(step.DisplayName || step.displayName);
                        normalizedStep.EntityType = normalizeValue(step.EntityType || step.entityType);
                        return normalizedStep as WorkflowStep;
                    });
                }
            }
        } catch (e) {
            console.error('Error parsing workflow config:', e);
        }

        return { steps, zSteps, stepAliases: aliases };
    }, [workflow]);

    // Initialize expanded state with first step
    useEffect(() => {
        if (steps.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            const stepIdValue = (steps[0] as any).Id;
            const firstId = typeof stepIdValue === 'object' && stepIdValue !== null
                ? (stepIdValue.Id || stepIdValue.$ref || String(stepIdValue))
                : String(stepIdValue || '0');
            setExpandedSteps([firstId]);
        }
    }, [steps]);

    // Parse step config to get props
    const getStepProps = (step: WorkflowStep): ConfigProp[] => {
        const props: ConfigProp[] = [];
        
        if (step.Settings) {
            try {
                const stepConfig = typeof step.Settings === 'string' ? JSON.parse(step.Settings) : step.Settings;
                if (stepConfig.props && Array.isArray(stepConfig.props)) {
                    // Normalize prop properties that might be reference objects
                    const normalizedProps = stepConfig.props.map((prop: any) => {
                        const normalizedProp: any = { ...prop };
                        // Normalize entityType if it's a reference object
                        if (normalizedProp.entityType && typeof normalizedProp.entityType === 'object') {
                            normalizedProp.entityType = normalizedProp.entityType.EntityType || 
                                                         normalizedProp.entityType.Id || 
                                                         normalizedProp.entityType.DisplayName || 
                                                         String(normalizedProp.entityType);
                        }
                        // Ensure name is a string
                        if (normalizedProp.name && typeof normalizedProp.name === 'object') {
                            normalizedProp.name = normalizedProp.name.name || 
                                                  normalizedProp.name.Id || 
                                                  String(normalizedProp.name);
                        }
                        return normalizedProp;
                    });
                    props.push(...normalizedProps);
                }
            } catch (e) {
                // Invalid JSON, skip
            }
        }
        
        return props;
    };

    // Get value override for a step property
    const getValueOverride = (step: WorkflowStep, propName: string): any => {
        if (step.valueOverrides && typeof step.valueOverrides === 'object') {
            return step.valueOverrides[propName];
        }
        return undefined;
    };

    const handleAccordionChange = (value: string | string[]) => {
        setExpandedSteps(Array.isArray(value) ? value : value ? [value] : []);
    };

    return (
        <Box p="md">
            <Title order={5} mb="sm">
                Workflow Steps ({steps.length})
            </Title>

            {steps.length === 0 ? (
                <Paper p="lg" ta="center" withBorder>
                    <Text c="dimmed">
                        This workflow has no steps configured.
                    </Text>
                </Paper>
            ) : (
                <Box>
                    <Accordion
                        multiple
                        value={expandedSteps}
                        onChange={handleAccordionChange}
                        chevron={<IconChevronDown size={16} />}
                    >
                        {steps.map((step, index) => {
                            const stepProps = getStepProps(step);
                            // Ensure step.Id is a string for lookups and keys
                            const stepIdValue = (step as any).Id;
                            const stepId = typeof stepIdValue === 'object' && stepIdValue !== null
                                ? (stepIdValue.Id || stepIdValue.$ref || String(stepIdValue))
                                : String(stepIdValue || '');
                            const stepAlias = stepAliases.get(stepId) || step.stepAlias;
                            // Safely extract DisplayName - handle both string and object cases
                            const stepDisplayNameValue = (step as any).DisplayName;
                            const stepDisplayName = typeof stepDisplayNameValue === 'object' && stepDisplayNameValue !== null
                                ? (stepDisplayNameValue.DisplayName || stepDisplayNameValue.Id || '')
                                : (stepDisplayNameValue || '');
                            // Safely extract EntityType - handle both string and object cases
                            const stepEntityTypeValue = (step as any).EntityType;
                            const stepEntityType = typeof stepEntityTypeValue === 'object' && stepEntityTypeValue !== null
                                ? (stepEntityTypeValue.EntityType || stepEntityTypeValue.Id || stepEntityTypeValue.DisplayName || '')
                                : (stepEntityTypeValue || '');
                            const displayName = stepAlias || stepDisplayName || `Step ${index + 1}`;
                            
                            // Safely compute config value for AceEditor (helper function, not a hook)
                            const getConfigValue = (config: any): string => {
                                if (config === null || config === undefined) {
                                    return '{}';
                                }
                                try {
                                    if (typeof config === 'string') {
                                        const trimmed = config.trim();
                                        return trimmed || '{}';
                                    } else if (typeof config === 'object') {
                                        const stringified = JSON.stringify(config, null, 2);
                                        return stringified || '{}';
                                    } else {
                                        const str = String(config);
                                        return str || '{}';
                                    }
                                } catch (e) {
                                    return '{}';
                                }
                            };
                            // Always ensure we have a valid string value for AceEditor
                            const safeConfigValue: string = (() => {
                                const config = step.Settings;
                                if (!config) {
                                    return '{}';
                                }
                                const computed = getConfigValue(config);
                                // Final safety check - must be a non-empty string
                                if (typeof computed === 'string' && computed.trim().length > 0) {
                                    return computed;
                                }
                                return '{}';
                            })();

                            const isExpanded = expandedSteps.includes(stepId);
                            
                            return (
                                <Accordion.Item key={stepId || index} value={stepId}>
                                    <Accordion.Control>
                                        <Group gap="sm" w="100%">
                                            <Badge
                                                variant="outline"
                                                size="sm"
                                                color="blue"
                                            >
                                                #{index + 1}
                                            </Badge>
                                            <Text flex={1}>
                                                {displayName}
                                            </Text>
                                            {stepAlias && stepDisplayName && stepAlias !== stepDisplayName && (
                                                <Text size="sm" c="dimmed">
                                                    ({stepDisplayName})
                                                </Text>
                                            )}
                                        </Group>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Box>
                                            {/* Step Information */}
                                            <Box mb="md">
                                                <Text size="sm" fw={600} mb="xs">
                                                    Step Information
                                                </Text>
                                                <Paper p="md" withBorder>
                                                    <Text size="sm">
                                                        <strong>ID:</strong> {stepId || 'N/A'}
                                                    </Text>
                                                    {stepDisplayName && (
                                                        <Text size="sm">
                                                            <strong>Display Name:</strong> {stepDisplayName}
                                                        </Text>
                                                    )}
                                                    {stepAlias && (
                                                        <Text size="sm">
                                                            <strong>Alias:</strong> {stepAlias}
                                                        </Text>
                                                    )}
                                                    {stepEntityType && (
                                                        <Text size="sm">
                                                            <strong>Entity Type:</strong> {stepEntityType}
                                                        </Text>
                                                    )}
                                                    {step.Action !== undefined && (
                                                        <Text size="sm">
                                                            <strong>Action:</strong> {step.Action ? 'Yes' : 'No'}
                                                        </Text>
                                                    )}
                                                    {step.Authorization !== undefined && (
                                                        <Text size="sm">
                                                            <strong>Authorization:</strong> {step.Authorization ? 'Yes' : 'No'}
                                                        </Text>
                                                    )}
                                                </Paper>
                                            </Box>

                                            {/* Configuration Properties */}
                                            {stepProps.length > 0 ? (
                                                <Box mb="md">
                                                    <Text size="sm" fw={600} mb="xs">
                                                        Configuration Properties
                                                    </Text>
                                                    <Paper p="md" withBorder>
                                                        {stepProps.map((prop, propIndex) => {
                                                            const overrideValue = getValueOverride(step, prop.name);
                                                            const displayValue = overrideValue !== undefined ? overrideValue : prop.defaultValue;

                                                            return (
                                                                <Box key={propIndex} mb="md" pb="md" style={{ borderBottom: propIndex < stepProps.length - 1 ? '1px solid var(--mantine-color-default-border)' : 'none' }}>
                                                                    <Text size="sm" fw={600} mb={4}>
                                                                        {prop.name}
                                                                    </Text>
                                                                    <Text size="xs" c="dimmed" mb={4} component="span" display="block">
                                                                        Type: {prop.controlType}
                                                                        {prop.entityType && (() => {
                                                                            const entityTypeValue = (prop as any).entityType;
                                                                            const entityTypeStr = typeof entityTypeValue === 'object' && entityTypeValue !== null
                                                                                ? (entityTypeValue.EntityType || entityTypeValue.Id || entityTypeValue.DisplayName || String(entityTypeValue))
                                                                                : String(entityTypeValue);
                                                                            return ` • Entity Type: ${entityTypeStr}`;
                                                                        })()}
                                                                    </Text>
                                                                    {displayValue !== undefined && displayValue !== null && displayValue !== '' && (
                                                                        <Group gap="xs" mt={4} align="flex-start">
                                                                            <Text size="sm" ff="monospace" flex={1} p="sm" bg="var(--mantine-color-default-hover)" style={{ borderRadius: 4 }}>
                                                                                {typeof displayValue === 'object' ? JSON.stringify(displayValue, null, 2) : String(displayValue)}
                                                                            </Text>
                                                                            {overrideValue !== undefined && (
                                                                                <Badge size="sm" color="blue">Override</Badge>
                                                                            )}
                                                                        </Group>
                                                                    )}
                                                                    {displayValue === undefined || displayValue === null || displayValue === '' ? (
                                                                        <Text size="sm" c="dimmed" fs="italic">
                                                                            No value set
                                                                        </Text>
                                                                    ) : null}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Paper>
                                                </Box>
                                            ) : (
                                                <Box mb="md">
                                                    <Text size="sm" c="dimmed">
                                                        This workflow activity has no configurable properties.
                                                    </Text>
                                                </Box>
                                            )}

                                            {/* Full Config JSON (collapsible) */}
                                            {step.Settings && (
                                                <Box>
                                                    <Text size="sm" fw={600} mb="xs">
                                                        Full Configuration (JSON)
                                                    </Text>
                                                    <Paper p="md" withBorder>
                                                        <Box style={{ 
                                                            border: '1px solid var(--mantine-color-default-border)', 
                                                            borderRadius: 4,
                                                            overflow: 'hidden',
                                                            maxHeight: '400px'
                                                        }}>
                                                            {isExpanded ? (
                                                                <Suspense fallback={<Box p="md" ta="center"><Loader size={24} /></Box>}>
                                                                    {safeConfigValue && typeof safeConfigValue === 'string' && safeConfigValue.trim() ? (
                                                                        <AceEditor
                                                                            key={`config-editor-${stepId}`}
                                                                            mode="json"
                                                                            theme={colorScheme === 'dark' ? 'monokai' : 'github'}
                                                                            value={safeConfigValue || '{}'}
                                                                            defaultValue="{}"
                                                                            name={`config-editor-${stepId}`}
                                                                            editorProps={{ $blockScrolling: true }}
                                                                            readOnly={true}
                                                                            width="100%"
                                                                            height="400px"
                                                                            fontSize={14}
                                                                            showPrintMargin={false}
                                                                            showGutter={true}
                                                                            highlightActiveLine={false}
                                                                            setOptions={{
                                                                                showLineNumbers: true,
                                                                                tabSize: 2,
                                                                                useWorker: false,
                                                                                foldStyle: 'markbeginend',
                                                                                showFoldWidgets: true
                                                                            }}
                                                                            style={{
                                                                                width: '100%'
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Box p="md" ta="center">
                                                                            <Text size="sm" c="dimmed">
                                                                                No configuration available
                                                                            </Text>
                                                                        </Box>
                                                                    )}
                                                                </Suspense>
                                                            ) : (
                                                                <Box p="md" ta="center">
                                                                    <Text size="sm" c="dimmed">
                                                                        Expand to view configuration
                                                                    </Text>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            )}
                                        </Box>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                </Box>
            )}
        </Box>
    );
};
